import { ParamMap, Params, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { User } from '../models';
import { AnalyticsService } from '../services/analytics/analytics.service';
import { LocalDataKey, LocalStorageService } from '../services/local-storage/local-storage.service';
import { MovingOrdersService } from '../services/moving-orders/moving-orders.service';
import { LocalUser } from '../services/users/users.service';
import { WarehouseOrdersService } from '../services/warehouse-orders/warehouse-orders.service';
import { buildQuery } from './url.utils';

export async function validateAndNavigate(
  user: User | null,
  localStorageService: LocalStorageService,
  router: Router,
  queryParamMap: ParamMap,
  analyticsService: AnalyticsService,
  movingOrdersService: MovingOrdersService,
  warehouseOrdersService: WarehouseOrdersService): Promise<void> {
  const origin = queryParamMap.get('origin');
  let redirectTo = queryParamMap.get('redirectTo');
  const queryParams: Params = {
    ...(origin && { origin }),
    ...(redirectTo && { redirectTo }),
  };
  if (!user) {
    await router.navigate(['/enrollment/sign-in'], { queryParams, queryParamsHandling: 'merge' });
    return;
  }
  localStorageService.set<LocalUser>(LocalDataKey.LocalUser, {
    bankValidated: user.bankValidated,
    email: user.email,
    emailValidated: user.emailValidated,
    enrollmentStatus: user.enrollmentStatus,
    fatherLastName: user.fatherLastName,
    id: user.id,
    motherLastName: user.motherLastName,
    name: user.name,
    phoneNumber: user.phoneNumber,
    profileDataCompleted: user.profileDataCompleted,
  });
  if (!user.emailValidated) {
    redirectTo = redirectTo ? decodeURIComponent(redirectTo) : null;
    if (redirectTo?.startsWith('/enrollment/send-verify-email')) {
      await router.navigateByUrl(redirectTo);
      return;
    }
    await router.navigate(['/enrollment/send-verify-email'], { queryParams, queryParamsHandling: 'merge' });
    return;
  }
  if (!user.name || !user.fatherLastName || !user.phoneNumber || !user.birthDate) {
    await router.navigate(['/enrollment/onboarding'], { queryParams, queryParamsHandling: 'merge' });
    return;
  }
  if (!user.bankValidated) {
    await router.navigate(['/enrollment/bank-validation'], { queryParams, queryParamsHandling: 'merge' });
    return;
  }

  await customNavigate(user, router, analyticsService, movingOrdersService, warehouseOrdersService, queryParamMap);
}

async function customNavigate(
  user: User | null,
  router: Router,
  analyticsService: AnalyticsService,
  movingOrdersService: MovingOrdersService,
  warehouseOrdersService: WarehouseOrdersService,
  queryParamMap: ParamMap,
): Promise<void> {
  if (!user) {
    return;
  }
  let redirectTo = queryParamMap.get('redirectTo');
  redirectTo = redirectTo ? decodeURIComponent(redirectTo) : null;
  if (!redirectTo) {
    await router.navigateByUrl('/private-site');
    return;
  }
  const navigationOrigin = queryParamMap.get('origin');
  let queryParams!: string;
  if (navigationOrigin && ['moving', 'warehouse'].includes(navigationOrigin)) {
    const { email, fatherLastName, id, name, phoneNumber } = user;
    const externalReference = navigationOrigin === 'moving' ? movingOrdersService.createId() : warehouseOrdersService.createId();
    const reference = environment.partners[navigationOrigin as keyof typeof environment.partners].reference;
    queryParams = buildQuery({
      email,
      external_reference: externalReference,
      ref: reference,
      user_id: id,
      ...(name && { name }),
      ...(fatherLastName && { last_name: fatherLastName }),
      ...(phoneNumber && { cellphone: phoneNumber.substring(3) }), // TODO: this should be truncated based on the phone country code
    });
  }
  if (redirectTo.startsWith('/')) {
    await router.navigateByUrl(redirectTo);
  } else {
    const params = queryParams ? `?${queryParams}` : '';
    window.location.href = `${redirectTo}${params}`;
    if (navigationOrigin === 'moving') {
      analyticsService.logMovingEvent({ step: 'start_quote' });
    } else if (navigationOrigin === 'warehouse') {
      analyticsService.logWarehouseEvent({ step: 'start_quote' });
    }
  }
}

export function brevoIdentify(user: Partial<Pick<User, 'bankValidated' | 'emailValidated' | 'enrollmentStatus' | 'id' | 'fatherLastName' | 'motherLastName' | 'name' | 'phoneNumber' | 'profileDataCompleted'>> & { email: string; } | null): void {
  if (!window.sib || !window.Brevo || !window.Brevo.push) {
    return;
  }
  const email = user?.email ?? '';
  const attributes = user ? {
    ...(user.bankValidated !== undefined && { user_bank_validated: `${user.bankValidated}` }),
    ...(user.email && { user_email: user.email }),
    ...(user.emailValidated !== undefined && { user_email_validated: `${user.emailValidated}` }),
    ...(user.enrollmentStatus && { user_enrollment_status: user.enrollmentStatus }),
    ...(user.fatherLastName && {
      user_father_last_name: user.fatherLastName,
      LASTNAME: user.fatherLastName,
    }),
    ...(user.id && { user_id: user.id }),
    ...(user.motherLastName && { user_mother_last_name: user.motherLastName }),
    ...(user.name && {
      user_name: user.name,
      FIRSTNAME: user.name,
    }),
    ...(user.phoneNumber && {
      user_phone_number: user.phoneNumber,
      SMS: user.phoneNumber,
      WHATSAPP: user.phoneNumber,
    }),
    ...(user.profileDataCompleted !== undefined && { user_profile_data_completed: `${user.profileDataCompleted}` }),
  } : {};

  window.sib.email_id = email;
  window.Brevo.push([
    'identify', {
      attributes,
      identifiers: {
        email_id: email,
      },
    },
  ]);
}